/**
 * Wraps a promise with a timeout. If the promise does not settle within the specified time,
 * the returned promise rejects with a timeout error.
 *
 * @param p The promise to wrap
 * @param options.ms Optional timeout in milliseconds. (default value is 3000 ms)
 * @param options.message Optional timeout error message
 */
export function withTimeout<T>(
  p: Promise<T>,
  options?: {
    ms?: number;
    message?: string;
  }
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout>;
  const ms = options?.ms ?? 5000;
  const message = options?.message || 'Operation timed out';

  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), ms);
  });

  const wrapped = Promise.resolve(p).finally(() => {
    clearTimeout(timeoutId);
  });

  return Promise.race([wrapped, timeout]) as Promise<T>;
}

/**
 * Method decorator that applies a timeout to async methods.
 * @param options.ms Optional timeout duration in milliseconds (default is 3000 ms)
 * @param options.message Optional custom timeout message
 * @returns Method decorator
 */
export function timeout(options?: {
  ms?: number;
  message?: string;
}): MethodDecorator {
  return function (
    target: Object,
    propertyKey: string | symbol,
    descriptor: TypedPropertyDescriptor<any>
  ): TypedPropertyDescriptor<any> | void {
    if (!descriptor || typeof descriptor.value !== 'function') {
      throw new Error('@timeout can only be applied to methods');
    }

    const originalMethod = descriptor.value as (...args: unknown[]) => unknown;

    // Use Proxy to intercept method calls and apply timeout only if result is a Promise
    descriptor.value = new Proxy(originalMethod, {
      apply(target, thisArg, args) {
        const result = Reflect.apply(target, thisArg, args);
        // If the result is a Promise, apply the timeout
        if (result && typeof (result as Promise<unknown>).then === 'function') {
          return withTimeout(result as Promise<unknown>, options);
        }
        // Otherwise, warn that the timeout decorator was applied to a sync method
        console.warn(
          `@timeout decorator applied to non-async method: ${String(
            propertyKey
          )}`
        );
        return result;
      }
    });
    return descriptor;
  };
}

/**
 * Class decorator that applies a timeout to all async methods in the class.
 * @param options.ms Optional timeout duration in milliseconds (default is 3000 ms)
 * @param options.message Optional custom timeout message
 * @returns Class decorator
 */
export function timeoutAll(options?: {
  ms?: number;
  message?: string;
}): ClassDecorator {
  return function <T extends Function>(constructor: T): T {
    // Wrap static methods
    const staticPropertyNames = Object.getOwnPropertyNames(constructor);
    staticPropertyNames.forEach((propertyName) => {
      if (
        propertyName === 'prototype' ||
        propertyName === 'length' ||
        propertyName === 'name'
      )
        return;

      const descriptor = Object.getOwnPropertyDescriptor(
        constructor,
        propertyName
      );
      if (!descriptor || typeof descriptor.value !== 'function') return;

      const originalMethod = descriptor.value;

      (constructor as any)[propertyName] = function (...args: any[]) {
        const result = originalMethod.apply(this, args);

        if (result && typeof (result as any).then === 'function') {
          return withTimeout(result as Promise<any>, options);
        }

        return result;
      };
    });

    // Wrap instance methods on the prototype
    const proto = constructor.prototype;
    const propertyNames = Object.getOwnPropertyNames(proto);

    propertyNames.forEach((propertyName) => {
      if (propertyName === 'constructor') return;

      const descriptor = Object.getOwnPropertyDescriptor(proto, propertyName);
      if (!descriptor || typeof descriptor.value !== 'function') return;

      const originalMethod = descriptor.value;

      proto[propertyName] = function (...args: any[]) {
        const result = originalMethod.apply(this, args);

        if (result && typeof (result as any).then === 'function') {
          return withTimeout(result as Promise<any>, options);
        }

        return result;
      };
    });

    return constructor;
  };
}
